"use client";

import { Badge } from "@/components/ui/primitives/badge";
import { subscribeToPositionUpdates } from "@/lib/utils/floating-position";
import { cn } from "@/lib/utils/general-utils";
import { useVirtualizer } from "@tanstack/react-virtual";
import {
  Check as CheckIcon,
  ChevronsUpDown as ChevronUpDownIcon,
  Search as MagnifyingGlassIcon,
  X as XMarkIcon,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import { createPortal } from "react-dom";

// =============================================================================
// TYPES
// =============================================================================

export interface SearchableSelectOption {
  value: string;
  label: string;
  /** Optional grouping key — options with the same group are rendered under a shared header. */
  group?: string;
  /** Secondary text shown right-aligned (e.g. "CSA Group I 20mm"). */
  meta?: string;
  /** When true, the option row uses a highlighted background (e.g. Live version). */
  highlighted?: boolean;
  /** Optional badge count shown next to the label. */
  count?: number;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface BaseProps {
  options: SearchableSelectOption[];
  /** Placeholder shown when nothing is selected. */
  placeholder?: string;
  /** Placeholder inside the search box. */
  searchPlaceholder?: string;
  /** Show the inline search input inside the dropdown (defaults to true). */
  searchable?: boolean;
  /** Show a clear button on the trigger when a value is selected. */
  showClear?: boolean;
  /** Show the up/down chevron on the trigger (defaults to true). */
  showTriggerIcon?: boolean;
  /** Show count badges on options that have a `count` field. */
  showCounts?: boolean;
  /** Trigger size — `"sm"` gives a compact trigger for tight contexts like table headers. */
  size?: "default" | "sm";
  /**
   * When false, the closed single-select label is not ellipsized.
   * Defaults to true so existing triggers keep truncating in narrow tracks.
   */
  clipSelectedLabel?: boolean;
  /** How the trigger sizes its min-width. `"selected"` fits the current value; `"widestOption"` expands to the widest option label. */
  fitToContent?: "selected" | "widestOption";
  loading?: boolean;
  disabled?: boolean;
  className?: string;
  /** Extra classes merged onto the dropdown panel (e.g. to override max-height). */
  dropdownClassName?: string;
  /** Maximum width of the dropdown panel in pixels. Defaults to 480. */
  maxDropdownWidth?: number;
  /** Floor for the dropdown panel width in pixels. Defaults to 0 (anchor/content). */
  minDropdownWidth?: number;
  /** Accessible label rendered above the trigger. */
  label?: string;
  /** Validation error message. */
  error?: string;
  /** When false, still marks the trigger invalid but does not render the message (parent owns layout). Defaults to true. */
  showErrorMessage?: boolean;
  "aria-label"?: string;
  /** PostHog click attribute rendered on the trigger. */
  "data-attr"?: string;
  /** Focus the trigger button on mount (useful for modals that should focus the first field). */
  autoFocus?: boolean;
  /** Called when the search input changes. Use with `asyncFiltering` for server-side search. */
  onSearchChange?: (query: string) => void;
  /** When true, skips local filtering — the parent is responsible for providing pre-filtered options. */
  asyncFiltering?: boolean;
  /** Position of the `meta` text in the dropdown options. Defaults to `"inline"` (right-aligned). */
  metaPosition?: "inline" | "below";
  /** Fired after a single-select value is committed (dropdown closes). Useful for auto-advancing focus. */
  onValueCommit?: () => void;
  /** Fired when the dropdown opens or closes. */
  onOpenChange?: (open: boolean) => void;
  /** Optional content rendered below the search box (e.g. filter chips). */
  dropdownHeader?: React.ReactNode;
  /** When true and a value is selected, prefix the trigger with `{placeholder}: `. */
  showFieldPrefix?: boolean;
  /** Custom multi-select trigger pills. Replaces the default per-value chips. */
  renderPills?: (args: { disabled: boolean }) => React.ReactNode;
  /**
   * Labels of the pills actually shown on the trigger. Used for width and
   * aria-label when they differ from `value` (e.g. Pass / Not assigned).
   */
  pillLabels?: readonly string[];
  /** When true, the trigger shows a selection even if `value` is empty. */
  hasSelection?: boolean;
  /** Trigger clear (X). Defaults to emptying `value`. */
  onClear?: () => void;
  /** Dim the option list without blocking clicks. */
  listMuted?: boolean;
}

interface SingleSelectProps extends BaseProps {
  multiple?: false;
  value: string;
  onChange: (value: string) => void;
}

interface MultiSelectProps extends BaseProps {
  multiple: true;
  value: string[];
  onChange: (value: string[]) => void;
}

export type SearchableSelectProps = SingleSelectProps | MultiSelectProps;

const MAX_DROPDOWN_WIDTH = 480;
/** Open-menu floor/cap for Specification (Pass / Fail / Not assigned on one row). */
export const FILTER_DROPDOWN_MIN_WIDTH = 380;
export const FILTER_DROPDOWN_MAX_WIDTH = 520;
/** Tighter open-menu size for name-only chips (Facility, Aggregate). */
export const NAME_FILTER_DROPDOWN_MIN_WIDTH = 280;
export const NAME_FILTER_DROPDOWN_MAX_WIDTH = 400;
/** Horizontal padding on dropdown option rows: pl-9 + pr-4. */
const DROPDOWN_ROW_PADDING_X = 52;
/** Chip padding + remove icon so fitToContent is not based on label text alone. */
const MULTI_CHIP_CHROME_PX = 28;
/** Horizontal gap between pills (`gap-1`). */
const MULTI_CHIP_GAP_PX = 4;
const EMPTY_GROUP_NAMES: string[] = [];
const EMPTY_SELECTED_VALUES: string[] = [];
/** Lists this large paint via TanStack Virtual instead of mounting every row. */
const VIRTUALIZE_THRESHOLD = 50;
const OPTION_ROW_HEIGHT = 36;
/** Single-line row + meta line (`text-[11px] mt-0.5`). */
const OPTION_ROW_WITH_META_HEIGHT = 56;
/** Group header: pt-2.5 + text-[10px] + pb-1. */
const GROUP_HEADER_HEIGHT = 28;
/** Options scroller cap — keep in sync with virtualizer `initialRect`. */
const DROPDOWN_LIST_MAX_HEIGHT = 224;
/** Longest labels are sizer-measured; string length is a cheap pre-filter. */
const MAX_SIZER_CANDIDATES = 8;

type VirtualListItem =
  | { kind: "header"; key: string; group: string }
  | { kind: "option"; key: string; option: SearchableSelectOption };

function flattenVirtualItems(
  filtered: SearchableSelectOption[],
  grouped: Map<string, SearchableSelectOption[]> | null,
): VirtualListItem[] {
  if (!grouped) {
    return filtered.map((option) => ({
      kind: "option" as const,
      key: option.value,
      option,
    }));
  }

  const items: VirtualListItem[] = [];
  for (const [group, groupOpts] of grouped) {
    if (group) {
      items.push({ kind: "header", key: `group:${group}`, group });
    }
    for (const option of groupOpts) {
      items.push({ kind: "option", key: option.value, option });
    }
  }
  return items;
}

function canMeasureVirtualRows(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof ResizeObserver !== "undefined" &&
    !navigator.userAgent.includes("Firefox")
  );
}

function GroupHeader({ group }: { group: string }) {
  return (
    <div className="px-3 pt-2.5 pb-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
        {group}
      </span>
    </div>
  );
}

function syncSizerFont(sizer: HTMLSpanElement, source: HTMLElement) {
  const style = getComputedStyle(source);
  sizer.style.fontFamily = style.fontFamily;
  sizer.style.fontSize = style.fontSize;
  sizer.style.fontWeight = "500";
  sizer.style.letterSpacing = style.letterSpacing;
  sizer.style.lineHeight = style.lineHeight;
}

function optionMeasureLength(opt: SearchableSelectOption): number {
  return (
    opt.label.length +
    (opt.meta?.length ?? 0) +
    (opt.count !== undefined ? String(opt.count).length : 0)
  );
}

function pickWidestOptionCandidates(
  options: SearchableSelectOption[],
  limit: number = MAX_SIZER_CANDIDATES,
): SearchableSelectOption[] {
  if (options.length <= limit) return options;
  return [...options]
    .sort((a, b) => optionMeasureLength(b) - optionMeasureLength(a))
    .slice(0, limit);
}

function measureDropdownContentWidthFromOptions(
  sizer: HTMLSpanElement,
  trigger: HTMLElement,
  candidateOptions: SearchableSelectOption[],
  config: {
    metaPosition: "inline" | "below";
    showCounts: boolean;
    searchable: boolean;
    searchPlaceholder: string;
    multiple: boolean;
    optionCount: number;
    groupNames: string[];
  },
): number {
  syncSizerFont(sizer, trigger);

  let maxWidth = 0;
  const textWidth = (text: string) => {
    sizer.textContent = text;
    return sizer.offsetWidth;
  };

  for (const opt of candidateOptions) {
    const labelWidth = textWidth(opt.label);
    let rowWidth = labelWidth + DROPDOWN_ROW_PADDING_X;

    if (config.metaPosition === "below" && opt.meta) {
      rowWidth =
        Math.max(labelWidth, textWidth(opt.meta)) + DROPDOWN_ROW_PADDING_X;
    } else if (opt.meta) {
      rowWidth = labelWidth + 8 + textWidth(opt.meta) + DROPDOWN_ROW_PADDING_X;
    }

    if (config.showCounts && opt.count !== undefined) {
      rowWidth += 8 + textWidth(String(opt.count)) + 20;
    }

    maxWidth = Math.max(maxWidth, rowWidth);
  }

  for (const group of config.groupNames) {
    maxWidth = Math.max(maxWidth, textWidth(group) + 24);
  }

  if (config.multiple && config.optionCount > 0) {
    const statusText = `${config.optionCount} of ${config.optionCount} selected`;
    maxWidth = Math.max(
      maxWidth,
      textWidth(statusText) + textWidth("Select All") + 48,
    );
  }

  if (config.searchable) {
    maxWidth = Math.max(maxWidth, textWidth(config.searchPlaceholder) + 52);
  }

  return maxWidth;
}

function computeDropdownGeometry(
  anchorRect: DOMRect,
  contentWidth: number,
  maxWidth: number = MAX_DROPDOWN_WIDTH,
  minWidth: number = 0,
): { width: number; left: number } {
  const viewportW = window.innerWidth;
  const width = Math.min(
    Math.max(anchorRect.width, contentWidth, minWidth),
    maxWidth,
    viewportW - 16,
  );

  let left = anchorRect.left;
  if (left + width > viewportW - 8) {
    left = Math.max(8, viewportW - width - 8);
  }

  return { width, left };
}

// =============================================================================
// COMPONENT
// =============================================================================

export function SearchableSelect(props: SearchableSelectProps) {
  const {
    options,
    placeholder = "Select…",
    searchPlaceholder = "Search…",
    disabled = false,
    loading = false,
    className,
    dropdownClassName,
    label,
    error,
    showErrorMessage = true,
    size = "default",
    clipSelectedLabel = true,
    "aria-label": ariaLabel,
    "data-attr": dataAttr,
    autoFocus = false,
    onSearchChange,
    asyncFiltering = false,
    metaPosition = "inline",
    onValueCommit,
    onOpenChange,
    dropdownHeader,
    showFieldPrefix = false,
    renderPills,
    pillLabels,
    hasSelection,
    onClear,
    listMuted = false,
  } = props;

  const multiple = props.multiple === true;
  const showTriggerIcon = props.showTriggerIcon ?? true;
  const AUTO_SEARCH_THRESHOLD = 5;
  const searchable =
    props.searchable ?? options.length >= AUTO_SEARCH_THRESHOLD;
  const showClear = props.showClear ?? true;
  const showCounts = props.showCounts ?? false;
  const isSmall = size === "sm";
  const fitToContent = props.fitToContent ?? "selected";
  const maxDropdownWidth = props.maxDropdownWidth ?? MAX_DROPDOWN_WIDTH;
  const minDropdownWidth = props.minDropdownWidth ?? 0;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);
  const triggerRef = useRef<HTMLElement>(null);
  const sizerRef = useRef<HTMLSpanElement>(null);
  const dropdownSizerRef = useRef<HTMLSpanElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const contentWidthRef = useRef(0);
  const anchorRectRef = useRef<DOMRect | null>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const [triggerContentMinWidth, setTriggerContentMinWidth] = useState<
    number | undefined
  >();

  // Close dropdown and clear search when it closes
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open && searchable) {
      requestAnimationFrame(() => {
        searchInputRef.current?.focus({ preventScroll: true });
      });
    }
  }, [open, searchable]);

  // Click-outside handler (checks both trigger container and portaled dropdown)
  useEffect(() => {
    if (!open) return;
    function handlePointerDown(e: PointerEvent) {
      const target = e.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !dropdownRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  // Escape key handler
  useEffect(() => {
    if (!open) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
      }
    }
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [open]);

  // Auto-focus the trigger when the component mounts with autoFocus enabled
  useEffect(() => {
    if (autoFocus && !disabled && !loading) {
      requestAnimationFrame(() => {
        triggerRef.current?.focus();
      });
    }
  }, [autoFocus, disabled, loading]);

  // Notify parent of search changes for async/server-side filtering
  useEffect(() => {
    if (onSearchChange) onSearchChange(query);
  }, [query, onSearchChange]);

  // ---- Filtered options (search across label, group, and meta) --------------
  const filtered = useMemo(() => {
    if (asyncFiltering) return options;
    if (!query.trim()) return options;
    const q = query.toLowerCase();
    return options.filter(
      (o) =>
        o.label.toLowerCase().includes(q) ||
        o.group?.toLowerCase().includes(q) ||
        o.meta?.toLowerCase().includes(q),
    );
  }, [options, query, asyncFiltering]);

  // ---- Group options if any have a `group` field ----------------------------
  const hasGroups = useMemo(() => options.some((o) => o.group), [options]);

  const groupedFiltered = useMemo(() => {
    if (!hasGroups) return null;
    const groups = new Map<string, SearchableSelectOption[]>();
    for (const opt of filtered) {
      const key = opt.group || "";
      const arr = groups.get(key) ?? [];
      arr.push(opt);
      groups.set(key, arr);
    }
    return groups;
  }, [filtered, hasGroups]);

  const dropdownGroupNames = useMemo(() => {
    if (!hasGroups) return EMPTY_GROUP_NAMES;
    return [
      ...new Set(
        options
          .map((opt) => opt.group || "")
          .filter((group) => group.length > 0),
      ),
    ];
  }, [options, hasGroups]);

  const widestOptionCandidates = useMemo(
    () => pickWidestOptionCandidates(options),
    [options],
  );

  const dropdownMeasureConfig = useMemo(
    () => ({
      metaPosition,
      showCounts,
      searchable,
      searchPlaceholder,
      multiple,
      optionCount: options.length,
      groupNames: dropdownGroupNames,
    }),
    [
      metaPosition,
      showCounts,
      searchable,
      searchPlaceholder,
      multiple,
      options.length,
      dropdownGroupNames,
    ],
  );

  const measureContentWidth = useCallback(() => {
    const sizer = dropdownSizerRef.current;
    const trigger = triggerRef.current;
    if (!sizer || !trigger) return 0;
    return measureDropdownContentWidthFromOptions(
      sizer,
      trigger,
      widestOptionCandidates,
      dropdownMeasureConfig,
    );
  }, [dropdownMeasureConfig, widestOptionCandidates]);

  // Position only — never remasures option labels. Width comes from contentWidthRef.
  const applyDropdownPosition = useCallback(() => {
    const anchor = triggerRef.current ?? containerRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    anchorRectRef.current = rect;
    const viewportH = window.innerHeight;
    const spaceBelow = viewportH - rect.bottom;
    const spaceAbove = rect.top;
    const maxH = 320; // matches max-h-80

    const preferBelow = spaceBelow >= maxH || spaceBelow >= spaceAbove;

    const { width, left } = computeDropdownGeometry(
      rect,
      contentWidthRef.current,
      maxDropdownWidth,
      minDropdownWidth,
    );

    setDropdownStyle({
      position: "fixed",
      width,
      left,
      zIndex: 300,
      ...(preferBelow
        ? { top: rect.bottom + 4 }
        : { bottom: viewportH - rect.top + 4 }),
    });
  }, [maxDropdownWidth, minDropdownWidth]);

  useLayoutEffect(() => {
    if (!open) {
      anchorRectRef.current = null;
      return;
    }
    contentWidthRef.current = measureContentWidth();
    const anchor = triggerRef.current ?? containerRef.current;
    if (!anchor) return;
    return subscribeToPositionUpdates(anchor, applyDropdownPosition);
  }, [open, measureContentWidth, applyDropdownPosition]);

  const virtualItems = useMemo(
    () => flattenVirtualItems(filtered, groupedFiltered),
    [filtered, groupedFiltered],
  );

  const shouldVirtualize = filtered.length >= VIRTUALIZE_THRESHOLD;
  const optionRowEstimate =
    metaPosition === "below" ? OPTION_ROW_WITH_META_HEIGHT : OPTION_ROW_HEIGHT;

  const estimateVirtualRowSize = useCallback(
    (index: number) => {
      const item = virtualItems[index];
      if (item?.kind === "header") return GROUP_HEADER_HEIGHT;
      return optionRowEstimate;
    },
    [virtualItems, optionRowEstimate],
  );

  const measureVirtualRow = useCallback(
    (element: Element) => {
      const index = Number(element.getAttribute("data-index"));
      const fallback = estimateVirtualRowSize(
        Number.isFinite(index) ? index : 0,
      );
      const rectHeight = element.getBoundingClientRect().height;
      if (rectHeight > 0) return rectHeight;
      const scrollHeight =
        element instanceof HTMLElement ? element.scrollHeight : 0;
      return scrollHeight > 0 ? scrollHeight : fallback;
    },
    [estimateVirtualRowSize],
  );

  const rowVirtualizer = useVirtualizer({
    count: shouldVirtualize ? virtualItems.length : 0,
    getScrollElement: () => listRef.current,
    estimateSize: estimateVirtualRowSize,
    measureElement: canMeasureVirtualRows() ? measureVirtualRow : undefined,
    overscan: 8,
    enabled: open && shouldVirtualize,
    initialRect: { width: 400, height: DROPDOWN_LIST_MAX_HEIGHT },
    getItemKey: (index) => virtualItems[index]?.key ?? index,
    paddingStart: 4,
    paddingEnd: 4,
    observeElementRect: (instance, cb) => {
      const element = instance.scrollElement as HTMLElement | null;
      if (!element) return;
      const notify = () => {
        cb({
          width: element.clientWidth || 400,
          height: element.clientHeight || DROPDOWN_LIST_MAX_HEIGHT,
        });
      };
      notify();
      if (typeof ResizeObserver === "undefined") return;
      const observer = new ResizeObserver(notify);
      observer.observe(element);
      return () => observer.disconnect();
    },
  });

  // ---- Display text ---------------------------------------------------------
  const resolveOptionLabel = useCallback(
    (val: string) => {
      const match = options.find((o) => o.value === val);
      if (match) return match.label;
      return /^[0-9a-f-]{20,}$/i.test(val) ? "Loading\u2026" : val;
    },
    [options],
  );

  const displayText = useMemo(() => {
    if (loading) return "Loading…";
    const { value } = props;
    if (Array.isArray(value) || !value) return placeholder;
    return resolveOptionLabel(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value, resolveOptionLabel, placeholder, loading]);

  const selectedValues = Array.isArray(props.value)
    ? props.value
    : EMPTY_SELECTED_VALUES;
  const selectValue = props.value;
  const hasValue =
    hasSelection ??
    (Array.isArray(selectValue)
      ? selectValue.length > 0
      : Boolean(selectValue));
  const showPills = multiple && hasValue && !loading;
  // Shrink-wrapped single-selects report the CSS min-width floor on first
  // layout (~140px). Capping to that locks the trigger and truncates labels.
  // Multi-select pills and widestOption must grow with content instead.
  const growWithContent =
    (multiple && hasValue) || fitToContent === "widestOption";

  const selectedCount = Array.isArray(props.value) ? props.value.length : 0;

  const triggerPillLabels = useMemo(() => {
    if (pillLabels) return [...pillLabels];
    if (multiple) return selectedValues.map((val) => resolveOptionLabel(val));
    return [];
  }, [pillLabels, multiple, selectedValues, resolveOptionLabel]);

  const triggerAriaLabel = (() => {
    const base = ariaLabel ?? placeholder;
    if (!showFieldPrefix || !hasValue || loading) return base;
    if (multiple) {
      if (triggerPillLabels.length === 0) return base;
      return `${placeholder}: ${triggerPillLabels.join(", ")}`;
    }
    return `${placeholder}: ${displayText}`;
  })();

  const triggerMeasureTexts = useMemo(() => {
    const texts = new Set<string>([placeholder]);
    if (fitToContent === "widestOption") {
      for (const option of options) {
        texts.add(
          showFieldPrefix ? `${placeholder}: ${option.label}` : option.label,
        );
      }
    }
    if (multiple) {
      for (const label of triggerPillLabels) {
        texts.add(label);
      }
    } else if (typeof selectValue === "string" && selectValue) {
      const optionLabel = resolveOptionLabel(selectValue);
      texts.add(
        showFieldPrefix ? `${placeholder}: ${optionLabel}` : optionLabel,
      );
    }
    if (loading) texts.add("Loading…");
    return Array.from(texts);
  }, [
    options,
    placeholder,
    triggerPillLabels,
    selectValue,
    resolveOptionLabel,
    loading,
    fitToContent,
    showFieldPrefix,
    multiple,
  ]);

  useLayoutEffect(() => {
    const sizer = sizerRef.current;
    const trigger = triggerRef.current;
    const container = containerRef.current;
    if (!sizer || !trigger) return;

    const measure = () => {
      const { paddingLeft, paddingRight } = getComputedStyle(trigger);
      const horizontalPadding =
        (Number.parseFloat(paddingLeft) || 0) +
        (Number.parseFloat(paddingRight) || 0);

      let maxTextWidth = 0;
      for (const text of triggerMeasureTexts) {
        sizer.textContent = text;
        maxTextWidth = Math.max(maxTextWidth, sizer.offsetWidth);
      }

      if (multiple && hasValue && triggerPillLabels.length > 0) {
        let rowWidth = 0;
        triggerPillLabels.forEach((label, index) => {
          sizer.textContent = label;
          rowWidth += sizer.offsetWidth + MULTI_CHIP_CHROME_PX;
          if (index > 0) rowWidth += MULTI_CHIP_GAP_PX;
        });
        if (showFieldPrefix) {
          sizer.textContent = `${placeholder}: `;
          rowWidth += sizer.offsetWidth + MULTI_CHIP_GAP_PX;
        }
        maxTextWidth = Math.max(maxTextWidth, rowWidth);
      }

      const floor = isSmall ? 110 : 140;
      const computed = Math.max(
        floor,
        Math.ceil(maxTextWidth + horizontalPadding),
      );
      const containerWidth = container?.offsetWidth;
      const capped = growWithContent
        ? computed
        : containerWidth && containerWidth > 0
          ? Math.min(computed, containerWidth)
          : computed;
      setTriggerContentMinWidth(capped);
    };

    measure();

    if (!container) return;
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [
    triggerMeasureTexts,
    isSmall,
    hasValue,
    growWithContent,
    showClear,
    showTriggerIcon,
    disabled,
    loading,
    multiple,
    triggerPillLabels,
    showFieldPrefix,
    placeholder,
  ]);

  // ---- Handlers -------------------------------------------------------------
  const handleOptionClick = useCallback(
    (optValue: string) => {
      if (props.multiple) {
        const current = props.value as string[];
        const next = current.includes(optValue)
          ? current.filter((v) => v !== optValue)
          : [...current, optValue];
        props.onChange(next);
      } else {
        (props.onChange as (v: string) => void)(optValue);
        setOpen(false);
        if (onValueCommit) {
          requestAnimationFrame(() => onValueCommit());
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.multiple, props.onChange, props.value, onValueCommit],
  );

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (onClear) {
        onClear();
        return;
      }
      if (props.multiple) {
        props.onChange([]);
      } else {
        (props.onChange as (v: string) => void)("");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.multiple, props.onChange, onClear],
  );

  const handleRemoveValue = useCallback(
    (e: React.MouseEvent, optValue: string) => {
      e.stopPropagation();
      e.preventDefault();
      if (!props.multiple) return;
      props.onChange(props.value.filter((v) => v !== optValue));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.multiple, props.onChange, props.value],
  );

  const handleTriggerClick = useCallback(() => {
    if (disabled || loading) return;
    setOpen((prev) => !prev);
  }, [disabled, loading]);

  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      if (disabled || loading) return;
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    },
    [disabled, loading],
  );

  const handleSelectAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (props.multiple) {
        const allValues = options
          .filter((o) => !o.disabled)
          .map((o) => o.value);
        props.onChange(allValues);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.multiple, props.onChange, options],
  );

  const handleClearAll = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (props.multiple) {
        props.onChange([]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.multiple, props.onChange],
  );

  const isSelected = useCallback(
    (val: string) => {
      return Array.isArray(props.value)
        ? props.value.includes(val)
        : props.value === val;
    },
    [props.value],
  );

  // ---- Option renderer (shared between flat & grouped) ----------------------
  const renderOption = (opt: SearchableSelectOption) => {
    const selected = isSelected(opt.value);
    return (
      <div
        key={opt.value}
        role="option"
        aria-selected={selected}
        aria-disabled={opt.disabled || undefined}
        tabIndex={-1}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => {
          if (!opt.disabled) handleOptionClick(opt.value);
        }}
        className={cn(
          "relative cursor-pointer select-none py-2 pl-9 pr-4 text-sm",
          "text-gray-700",
          opt.highlighted && "bg-accent text-accent-foreground",
          !opt.highlighted && "hover:bg-accent hover:text-accent-foreground",
          opt.disabled && "opacity-40 cursor-not-allowed",
        )}
        data-highlighted={opt.highlighted ? "true" : undefined}
      >
        <span className="absolute inset-y-0 left-0 flex items-center pl-2.5">
          {multiple ? (
            <span
              className={cn(
                "flex h-4 w-4 items-center justify-center rounded border transition-colors",
                selected
                  ? "border-primary bg-primary"
                  : "border-gray-300 bg-white",
              )}
              aria-hidden
            >
              {selected && <CheckIcon className="h-3 w-3 text-white" />}
            </span>
          ) : (
            selected && (
              <CheckIcon className="h-4 w-4 text-primary" aria-hidden />
            )
          )}
        </span>

        {metaPosition === "below" && opt.meta ? (
          <span className="flex min-w-0 flex-col">
            <span
              className={cn(
                "block truncate",
                selected && "font-medium text-gray-900",
              )}
            >
              {opt.icon && (
                <span className="mr-2 inline-flex align-text-bottom">
                  {opt.icon}
                </span>
              )}
              {opt.label}
            </span>
            <span className="text-[11px] text-text-tertiary mt-0.5">
              {opt.meta}
            </span>
          </span>
        ) : (
          <span className="flex items-center justify-between gap-2 min-w-0">
            <span
              className={cn(
                "block min-w-0 flex-1 truncate",
                selected && "font-medium text-gray-900",
              )}
            >
              {opt.icon && (
                <span className="mr-2 inline-flex align-text-bottom">
                  {opt.icon}
                </span>
              )}
              {opt.label}
            </span>

            {opt.meta && (
              <span className="text-[11px] text-text-tertiary flex-shrink-0">
                {opt.meta}
              </span>
            )}
            {showCounts && opt.count !== undefined && (
              <Badge color="zinc" size="sm" shape="pill">
                {opt.count}
              </Badge>
            )}
          </span>
        )}
      </div>
    );
  };

  // ---- Render ---------------------------------------------------------------
  return (
    <div
      ref={containerRef}
      data-slot="control"
      className={cn("relative", className)}
      style={
        growWithContent && triggerContentMinWidth
          ? { minWidth: triggerContentMinWidth }
          : undefined
      }
    >
      {/* Label */}
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      {/* Trigger + clear button wrapper */}
      <div className="relative">
        <span
          ref={dropdownSizerRef}
          aria-hidden
          className="pointer-events-none fixed whitespace-nowrap text-sm"
          style={{ top: -9999, left: -9999, visibility: "hidden" }}
        />
        <span
          ref={sizerRef}
          aria-hidden
          className={cn(
            "pointer-events-none invisible absolute whitespace-nowrap",
            isSmall ? "text-xs" : "text-sm",
          )}
        />
        {(() => {
          const triggerDisabled = disabled || loading;
          const triggerClassName = cn(
            "relative flex items-center gap-1.5 rounded-lg border bg-white text-left w-full max-w-full",
            isSmall
              ? "py-1 pl-2.5 text-xs min-w-[110px]"
              : "py-1.5 pl-3 text-sm min-w-[140px]",
            hasValue && showClear && !disabled
              ? showTriggerIcon
                ? "pr-14"
                : "pr-8"
              : showTriggerIcon
                ? "pr-8"
                : "pr-3",
            "border-gray-300 hover:border-gray-400 transition-colors",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
            triggerDisabled && "cursor-not-allowed opacity-50",
            open && "border-gray-400 ring-1 ring-gray-200",
            error && "border-red-500 hover:border-red-500",
          );
          const triggerStyle = triggerContentMinWidth
            ? { minWidth: triggerContentMinWidth }
            : undefined;
          const triggerContent = (
            <>
              <span
                className={cn(
                  "flex items-center",
                  showPills
                    ? "min-w-0 flex-nowrap gap-1 overflow-x-auto"
                    : growWithContent
                      ? "whitespace-nowrap"
                      : !clipSelectedLabel
                        ? // Clip without an ellipsis so the label stays off the clear control.
                          "min-w-0 overflow-hidden whitespace-nowrap"
                        : "min-w-0 truncate",
                  !hasValue && "text-text-tertiary",
                )}
              >
                {showFieldPrefix && hasValue && !loading && (
                  <span className="shrink-0 text-text-tertiary">
                    {placeholder}:{" "}
                  </span>
                )}
                {showPills
                  ? renderPills
                    ? renderPills({ disabled: triggerDisabled })
                    : selectedValues.map((val) => {
                        const pillLabel = resolveOptionLabel(val);
                        return (
                          <span
                            key={val}
                            className="inline-flex shrink-0 items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-foreground"
                          >
                            <span className="max-w-[12rem] truncate">
                              {pillLabel}
                            </span>
                            {!triggerDisabled && (
                              <button
                                type="button"
                                onClick={(e) => handleRemoveValue(e, val)}
                                className="inline-flex items-center justify-center rounded-sm text-muted-foreground transition-colors hover:text-foreground"
                                aria-label={`Remove ${pillLabel}`}
                              >
                                <XMarkIcon className="h-3 w-3" />
                              </button>
                            )}
                          </span>
                        );
                      })
                  : displayText}
              </span>

              {(loading || showTriggerIcon) && (
                <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                  {loading ? (
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary" />
                  ) : (
                    <ChevronUpDownIcon
                      className="h-4 w-4 text-gray-400"
                      aria-hidden="true"
                    />
                  )}
                </span>
              )}
            </>
          );

          if (multiple) {
            return (
              <div
                ref={(node) => {
                  triggerRef.current = node;
                }}
                role="combobox"
                tabIndex={triggerDisabled ? -1 : 0}
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-label={triggerAriaLabel}
                data-attr={dataAttr}
                aria-invalid={!!error || undefined}
                aria-disabled={triggerDisabled || undefined}
                onClick={handleTriggerClick}
                onKeyDown={handleTriggerKeyDown}
                style={triggerStyle}
                className={triggerClassName}
              >
                {triggerContent}
              </div>
            );
          }

          return (
            <button
              ref={(node) => {
                triggerRef.current = node;
              }}
              type="button"
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-label={triggerAriaLabel}
              data-attr={dataAttr}
              aria-invalid={!!error || undefined}
              disabled={triggerDisabled}
              onClick={handleTriggerClick}
              style={triggerStyle}
              className={triggerClassName}
            >
              {triggerContent}
            </button>
          );
        })()}

        {showClear && hasValue && !disabled && !loading && (
          <button
            type="button"
            onClick={handleClear}
            className={cn(
              "absolute inset-y-0 flex items-center px-1.5 z-10 group/clear",
              showTriggerIcon ? "right-6" : "right-2",
            )}
            aria-label="Clear selection"
          >
            <span className="flex items-center justify-center h-4 w-4 rounded-full bg-gray-200 group-hover/clear:bg-gray-400 transition-colors">
              <XMarkIcon className="h-2.5 w-2.5 text-text-tertiary group-hover/clear:text-white transition-colors" />
            </span>
          </button>
        )}
      </div>

      {/* Validation error */}
      {error && showErrorMessage && (
        <p className="mt-1 text-sm text-status-error-text">{error}</p>
      )}

      {/* Dropdown panel — portaled to body to escape parent overflow clipping */}
      {mounted &&
        createPortal(
          <div
            ref={dropdownRef}
            role="listbox"
            aria-multiselectable={multiple || undefined}
            aria-hidden={!open}
            onMouseDown={(e) => e.preventDefault()}
            style={
              open
                ? { ...dropdownStyle, willChange: "opacity" }
                : {
                    position: "fixed",
                    top: -9999,
                    left: -9999,
                    width: 0,
                    height: 0,
                    overflow: "hidden",
                    willChange: "opacity",
                  }
            }
            className={cn(
              "max-h-80 overflow-hidden rounded-lg",
              "bg-white shadow-lg ring-1 ring-gray-200 focus:outline-none",
              open
                ? "opacity-100 transition-opacity duration-100 pointer-events-auto"
                : "opacity-0 pointer-events-none",
              dropdownClassName,
            )}
          >
            {open && (
              <>
                {/* Search + optional header (filters) */}
                {(searchable || dropdownHeader) && (
                  <div className="sticky top-0 z-10 bg-white border-b border-gray-200">
                    {searchable && (
                      <div className="p-1.5">
                        <div className="relative">
                          <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <input
                            ref={searchInputRef}
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={searchPlaceholder}
                            className={cn(
                              "w-full rounded-md border border-gray-200 bg-gray-50 pl-8 pr-2.5 py-1.5 text-sm",
                              query ? "pr-7" : "",
                              "placeholder:text-gray-400 focus:border-ring focus:bg-white focus:outline-none focus:ring-1 focus:ring-ring",
                            )}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                          {query && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setQuery("");
                              }}
                              className="absolute inset-y-0 right-1.5 flex items-center"
                              aria-label="Clear search"
                            >
                              <XMarkIcon className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 transition-colors" />
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {dropdownHeader ? (
                      <div
                        className={cn(
                          "px-2 py-1.5",
                          searchable && "border-t border-gray-100",
                        )}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {dropdownHeader}
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Select All / Clear row for multi-select */}
                {multiple && (
                  <div className="flex items-center justify-between px-3 py-1.5 border-b border-gray-200 bg-gray-50/50">
                    <span className="text-[11px] text-text-tertiary">
                      {selectedCount} of {options.length} selected
                    </span>
                    <div className="flex items-center gap-2">
                      {selectedCount < options.length && (
                        <button
                          type="button"
                          onClick={handleSelectAll}
                          className="text-[11px] font-medium text-primary hover:text-primary/80"
                        >
                          Select All
                        </button>
                      )}
                      {selectedCount > 0 && (
                        <button
                          type="button"
                          onClick={handleClearAll}
                          className="text-[11px] font-medium text-status-error-text hover:text-red-800"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Options list */}
                <div
                  ref={listRef}
                  className={cn(
                    "overflow-auto",
                    !shouldVirtualize && "py-1",
                    listMuted && "opacity-50",
                  )}
                  style={{ maxHeight: DROPDOWN_LIST_MAX_HEIGHT }}
                >
                  {filtered.length === 0 ? (
                    <div className="px-3 py-6 text-center text-sm text-text-tertiary">
                      No matches
                    </div>
                  ) : shouldVirtualize ? (
                    <div
                      style={{
                        height: `${rowVirtualizer.getTotalSize()}px`,
                        width: "100%",
                        position: "relative",
                      }}
                    >
                      {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                        const item = virtualItems[virtualRow.index];
                        if (!item) return null;
                        return (
                          <div
                            key={virtualRow.key}
                            data-index={virtualRow.index}
                            ref={rowVirtualizer.measureElement}
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: `${virtualRow.size}px`,
                              transform: `translateY(${virtualRow.start}px)`,
                            }}
                          >
                            {item.kind === "header" ? (
                              <GroupHeader group={item.group} />
                            ) : (
                              renderOption(item.option)
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : groupedFiltered ? (
                    Array.from(groupedFiltered.entries()).map(
                      ([group, groupOpts]) => (
                        <div key={group}>
                          {group ? <GroupHeader group={group} /> : null}
                          {groupOpts.map(renderOption)}
                        </div>
                      ),
                    )
                  ) : (
                    filtered.map(renderOption)
                  )}
                </div>
              </>
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
